import { getPage } from 'constants/pages';
import _ from 'lodash';
import * as React from 'react';
import { generatePath, matchPath, Path, useLocation, useNavigate } from 'react-router-dom';
import { Region, RegionKey } from 'services/clients/regions';
import { useUserContext } from './UserContext';

type SetRegion = (region: Region) => void;

interface RegionContextValues {
  region: Region;
  setRegion: SetRegion;
  availableRegions: Region[];
}

const LOCAL_STORAGE_REGION_KEY = 'regionKey';

export const RegionContext = React.createContext<RegionContextValues | null>(null);

/**
 * Provides context to get and set the current region
 *
 * When explicitly setting a region local storage will be updated and if the current page is a
 * region-specific page it will navigate to the new region's page.
 *
 * Implicit region changes (i.e. via navigation) are also captured and stored in local storage.
 * @param props
 * @returns
 */
export const RegionContextProvider: React.FC<{ children: React.ReactNode }> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserContext();

  const [regionKeys, filteredRegions, regionMap] = React.useMemo(() => {
    const regions: Region[] = _.sortBy([...(user?.company?.port_collections || [])], 'label') || [];

    return [
      regions.map((r) => r.code),
      regions,
      regions.reduce<Partial<Record<RegionKey, Region>>>(
        (dict, region) => ({ ...dict, [region.code]: region }),
        {}
      ),
    ];
  }, [user]);

  // Extract the region from the route, if possible
  const regionFromRoute = React.useMemo(() => {
    const page = getPage(location.pathname);
    const params = page && matchPath(page.path, location.pathname)?.params;
    if (params?.region) {
      return filteredRegions.find((r) => r.route === params.region);
    }
    return undefined;
  }, [location.pathname, filteredRegions]);

  // Extract the region from local storage
  const getRegionFromLocalStorage = React.useCallback(() => {
    const key = localStorage.getItem(LOCAL_STORAGE_REGION_KEY);
    if (!key || !regionKeys.includes(key as RegionKey)) {
      return undefined;
    }
    return regionMap[key as RegionKey];
  }, [regionKeys, regionMap]);

  // Our fallback used when a page does not have a region in its route
  const [localRegion, setLocalRegion] = React.useState(
    getRegionFromLocalStorage() || filteredRegions[0]
  );

  // Explicitly set the region
  const setRegion: SetRegion = React.useCallback(
    (r) => {
      // If we're on a page with the region in the route, navigate to reflect the change
      const page = getPage(location.pathname);
      if (page) {
        const to: Partial<Path> = {
          pathname: generatePath(page.path, { region: r.route }),
        };
        navigate(to);
      }

      // Store the new region in localstorage
      localStorage.setItem(LOCAL_STORAGE_REGION_KEY, r.code);
      setLocalRegion(r);
    },
    [location.pathname, navigate]
  );

  // Capture implicit region changes caused by navigation
  React.useEffect(() => {
    setLocalRegion((value) => {
      if (regionFromRoute && regionFromRoute !== value) {
        return regionFromRoute;
      }
      return value;
    });
  }, [regionFromRoute]);

  // Whenever the list of available regions changes, we may need to update
  React.useEffect(() => {
    setLocalRegion((local) => {
      // Always try to load from the local storage preference first
      const region = getRegionFromLocalStorage();
      if (region && local.code !== region.code) {
        return region;
      }

      // Then fallback to the first available
      if (local && !regionKeys.includes(local.code)) {
        return filteredRegions[0];
      }

      // No change necessary
      return local;
    });
  }, [regionKeys, filteredRegions, setLocalRegion, getRegionFromLocalStorage]);

  const value = React.useMemo(() => {
    return {
      region: regionFromRoute || localRegion || filteredRegions[0],
      setRegion,
      availableRegions: filteredRegions,
    };
  }, [regionFromRoute, localRegion, setRegion, filteredRegions]);

  return <RegionContext.Provider value={value}>{props.children}</RegionContext.Provider>;
};

export function useRegionContext() {
  const context = React.useContext(RegionContext);
  if (context === null) {
    throw new Error('useRegionContext must be called within a RegionContextProvider');
  }

  return context;
}
